import Phaser from 'phaser';
import type { BiomeDecorationMotion } from '@/domain/types/biome';

type PageFlipMotion = Extract<BiomeDecorationMotion, { kind: 'pageFlip' }>;

interface BookEntry {
  base: Phaser.GameObjects.Image;
  page: Phaser.GameObjects.Image;
  shadow: Phaser.GameObjects.Ellipse;
  motion: PageFlipMotion;
  hingeX: number;
  hingeY: number;
  pageScaleX: number;
  pageScaleY: number;
  nextFlipAt: number;
}

/**
 * Ambientowe przewracanie stron otwartych książek.
 *
 * Książka jest nieruchomym obrazkiem, a osobna kartka zwęża się do zera i
 * rozszerza po drugiej stronie grzbietu. Jeden wspólny system pilnuje, aby na
 * mapie przewracała się maksymalnie jedna strona naraz.
 */
export class BookPageFlipSystem {
  private readonly entries: BookEntry[] = [];
  private activeEntry: BookEntry | null = null;
  private activeTween: Phaser.Tweens.Tween | null = null;
  private nextGlobalFlipAt = 0;

  constructor(private readonly scene: Phaser.Scene) {}

  add(base: Phaser.GameObjects.Image, motion: PageFlipMotion): void {
    if (!this.scene.textures.exists(motion.pageTextureKey)) return;

    const localX = motion.offsetX ?? 0;
    const localY = motion.offsetY ?? -1;
    const cos = Math.cos(base.rotation);
    const sin = Math.sin(base.rotation);
    const hingeX = base.x + localX * cos - localY * sin;
    const hingeY = base.y + localX * sin + localY * cos;

    const page = this.scene.add.image(hingeX, hingeY, motion.pageTextureKey);
    page
      .setOrigin(motion.hingeOriginX ?? 0.178, 0.5)
      .setDisplaySize(
        base.displayWidth * (motion.pageWidthRatio ?? 0.72),
        base.displayHeight * (motion.pageHeightRatio ?? 0.92),
      )
      .setRotation(base.rotation)
      .setDepth(base.depth + 0.2);

    const shadow = this.scene.add.ellipse(
      hingeX,
      hingeY + 3,
      base.displayWidth * 0.44,
      base.displayHeight * 0.17,
      0x4a2b1b,
      0,
    );
    shadow.setRotation(base.rotation).setDepth(base.depth + 0.1);

    const minInterval = motion.intervalMinMs ?? 4_500;
    const maxInterval = Math.max(minInterval, motion.intervalMaxMs ?? 9_000);
    const entry: BookEntry = {
      base,
      page,
      shadow,
      motion,
      hingeX,
      hingeY,
      pageScaleX: page.scaleX,
      pageScaleY: page.scaleY,
      nextFlipAt: this.scene.time.now + Phaser.Math.Between(
        Math.max(500, Math.round(minInterval * 0.35)),
        Math.max(700, Math.round(maxInterval * 0.8)),
      ),
    };
    this.entries.push(entry);

    if (motion.interactive !== false) {
      base.setInteractive({ useHandCursor: true });
      base.on('pointerdown', (
        _pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();
        this.requestFlip(entry);
      });
    }
  }

  update(time: number): void {
    if (this.activeEntry || time < this.nextGlobalFlipAt) return;

    let due: BookEntry | null = null;
    for (const entry of this.entries) {
      if (!entry.base.scene || time < entry.nextFlipAt) continue;
      if (!due || entry.nextFlipAt < due.nextFlipAt) due = entry;
    }
    if (due) this.play(due);
  }

  destroy(): void {
    this.activeTween?.remove();
    this.activeTween = null;
    this.activeEntry = null;
    for (const entry of this.entries) {
      entry.page.destroy();
      entry.shadow.destroy();
    }
    this.entries.length = 0;
  }

  private requestFlip(entry: BookEntry): void {
    if (this.activeEntry || !entry.base.scene) return;
    this.play(entry);
  }

  private play(entry: BookEntry): void {
    const duration = entry.motion.durationMs ?? 820;
    const liftPx = entry.motion.liftPx ?? 7;
    const progress = { value: 0 };

    this.activeEntry = entry;
    entry.nextFlipAt = Number.POSITIVE_INFINITY;
    entry.page.setVisible(true).setAlpha(1);

    this.activeTween = this.scene.tweens.add({
      targets: progress,
      value: 1,
      duration,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        const p = progress.value;
        const side = Math.cos(p * Math.PI);
        const lift = Math.sin(p * Math.PI);
        const fade = p > 0.9 ? Math.max(0, 1 - (p - 0.9) / 0.1) : 1;

        entry.page.setScale(
          entry.pageScaleX * side,
          entry.pageScaleY * (1 - lift * 0.06),
        );
        entry.page.setPosition(entry.hingeX, entry.hingeY - lift * liftPx);
        entry.page.setRotation(
          entry.base.rotation + Phaser.Math.DegToRad(Math.sin(p * Math.PI * 2) * 1.8),
        );
        entry.page.setAlpha(fade);

        entry.shadow.setPosition(
          entry.hingeX + Math.cos(entry.base.rotation) * side * entry.base.displayWidth * 0.11,
          entry.hingeY + 3 + Math.sin(entry.base.rotation) * side * entry.base.displayWidth * 0.11,
        );
        entry.shadow.setAlpha(lift * 0.16);
        entry.shadow.setScale(0.55 + lift * 0.45, 0.72 + lift * 0.28);
      },
      onComplete: () => {
        entry.shadow.setAlpha(0);
        entry.page
          .setPosition(entry.hingeX, entry.hingeY)
          .setRotation(entry.base.rotation)
          .setScale(entry.pageScaleX, entry.pageScaleY)
          .setAlpha(0);

        this.scene.tweens.add({
          targets: entry.page,
          alpha: 1,
          duration: 140,
          ease: 'Sine.easeOut',
        });

        const minInterval = entry.motion.intervalMinMs ?? 4_500;
        const maxInterval = Math.max(minInterval, entry.motion.intervalMaxMs ?? 9_000);
        entry.nextFlipAt = this.scene.time.now + Phaser.Math.Between(minInterval, maxInterval);
        this.nextGlobalFlipAt = this.scene.time.now + 650;
        this.activeTween = null;
        this.activeEntry = null;
      },
    });
  }
}
